<template>
	<div class="wrapper" :style="globalPaletteVars">
		<UiDialog v-if="idleStore.isModalVisible" style="z-index: 99">
			<template #default>
				<UiElement :settings="dialog.appearance.body.settings">
					<div class="d-flex flex-column align-items-center justify-content-center">
						<UiText :settings="heading1" class="text-center mb-4" :content="translations?.continueShopping || '{continueShopping}'" />

						<UiButton
							:settings="button1"
							@touchstart="idleStore.continueUsage"
							:content="translations?.proceedOrder || '{proceedOrder}'"
						/>

						<div class="py-4"/>

						<UiButton
							:settings="button2"
							:content="translations?.cancelOrder || '{cancelOrder}'"
							@touchstart="cancelOrder"
						/>
					</div>
				</UiElement>
			</template>
		</UiDialog>

		<router-view v-slot="{ Component }">
			<transition :name="transitionName">
				<component :is="Component" :key="$route.path" />
			</transition>
		</router-view>

		<div class="d-none" style="position: fixed; left: 0; top: 0; background: #fcfcfc">
			<p class="mb-0">Kiosk ID: {{ kioskId }}</p>

			<p class="mb-0">Account ID: {{ accountId }}</p>

			<p class="mb-0">Branch ID: {{ branchId }}</p>
		</div>

		<DialogMaxCount :show="isVisibleDialogMaxCount" />
	</div>
</template>

<script setup>
	import { watch, onMounted, onBeforeUnmount, onUnmounted, computed, watchEffect } from 'vue';
	import { useRoute } from 'vue-router';
	import { storeToRefs } from 'pinia';
	import i18n from '@/i18n';

	import { navigationDirection } from './router';
	import { useKioskStore } from '@/stores/useKioskStore';
	import { useThemeStore } from '@/stores/useThemeStore';
	import { useOrderStore } from '@/stores/useOrderStore';
	import { useIdleStore } from '@/stores/useIdleStore';
	import { useCashierStore } from '@/stores/legacy/cashier-store';
	import { useLegacyKioskStore } from '@/stores/legacy/kiosk-store';
	import { useBasketStore } from '@/stores/legacy/basket-store';
	import { useDialogStore } from '@/stores/useDialogStore';
	import { useTranslationsStore } from '@/stores/useTranslationsStore';

	import { useInheritedSettings } from '@/composables/useInheritedSettings';

	import UiDialog from '@/components/UI/UiDialog.vue';
	import UiButton from '@/components/UI/UiButton.vue';
	import UiElement from '@/components/UI/UiElement.vue';
	import UiText from '@/components/UI/UiText.vue';
	import DialogMaxCount from '@/components/Dialogs/DialogMaxCount.vue';

	const orderStore = useOrderStore();
	const themeStore = useThemeStore();
	const kioskStore = useKioskStore();
	const legacyKioskStore = useLegacyKioskStore();
	const cashierStore = useCashierStore();
	const basketStore = useBasketStore();
	const translationsStore = useTranslationsStore();
	const idleStore = useIdleStore();
	const dialogStore = useDialogStore();

	const { addProduct } = orderStore;
	const { showDialogStore } = dialogStore;

	const { theme } = storeToRefs(themeStore)
	const { cart } = storeToRefs(orderStore);
	const { translations } = storeToRefs(translationsStore);
	const { isEmbed } = storeToRefs(kioskStore);
	const { isVisibleDialogMaxCount } = storeToRefs(dialogStore);

	const { kioskId, accountId, branchId } = kioskStore;

	const transitionName = computed(() => {
		if (navigationDirection.value === 'none') return '';
		return navigationDirection.value === 'forward' ? 'slide-forward' : 'slide-backward';
	});

	const globalPaletteVars = computed(() => {
		if (!theme.value) return {};

		const { palette } = theme.value.general;

		return Object.fromEntries([
			...Object.entries(palette).map(([key, value]) => [`--palette-${ key }`, value]),
			['--palette-transparent', 'transparent']
		]);
	});

	const dialog = computed(() => theme.value.pages.dialogKioskTimeout);
	const heading1 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.heading1)) : null;
	const button1 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.button1)) : null;
	const button2 = dialog.value ? useInheritedSettings(computed(() => dialog.value.elements?.button2)) : null;

	const resetOnActivity = () => idleStore.resetTimer();

	watchEffect(() => {
		if (cart.value.length === 999) {
			showDialogStore();
		}
	});

	function cancelOrder() {
		idleStore.cancelUsage();
		orderStore.clearCart();
	}

	function preventEvent(e) {
		console.warn('Disabling events for iframe');

		e.stopPropagation();
		e.preventDefault();
	}

	function runLegacyFns() {
		const isLive = route.query.live === '1';

		legacyKioskStore.getKiosk(kioskId).then(() => {
			legacyKioskStore.checkSetup().then((check) => {
				if (check) {
					console.log("Need Setup");
				} else {
					const cancel = {
						type: "cancellation",
						response: {
							orderId: 0,
							cancellation: true
						}
					}
					basketStore.cancelTransaction(cancel);

					legacyKioskStore.getMenu()
					legacyKioskStore.getParameters();

					const excludedPaths = ['/payment-complete', '/payment-failed', '/end'];

					const pathParts = window.location.pathname.split('/');
					const currentPath = '/' + pathParts[pathParts.length - 1];

					// console.log("t1",currentPath);
					// console.log("t2",excludedPaths);

					if (!excludedPaths.includes(currentPath) && isLive){
						cashierStore.startTimer();
					}
				}
			})
		})
	}

	const route = useRoute();

	watch(() => route.params.locale, (newLocale) => {
		i18n.global.locale = newLocale || 'en';
	}, { immediate: true });


	onMounted(async () => {
		const BACKOFFICE = process.env.VUE_APP_BACKOFFICE;
		const ENV = process.env.VUE_APP_ENV;

		window.addEventListener('message', (event) => {
			if (window.self !== window.top) {
				isEmbed.value = true;
				themeStore.applyExternalTheme(event);

				window.addEventListener('click', preventEvent, true)
				window.addEventListener('touchstart', preventEvent, true)
				window.addEventListener('pointerdown', preventEvent, true)

				if (cart.value.length === 0) {
					addProduct({ label: 'Test Product 1', id: 0, price: 100 });
					addProduct({ label: 'Test Product 2', id: 1, price: 49 });
					addProduct({ label: 'Test Product 2', id: 1, price: 49 });
				}
			}
		});
		if (window.self !== window.top) {
			window.parent.postMessage('ready', BACKOFFICE);
		} else {
			if (ENV !== 'local') {
				const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
				events.forEach(event => window.addEventListener(event, resetOnActivity));
				resetOnActivity();
			}

			runLegacyFns();
		}
	});

	onBeforeUnmount(() => {
		window.removeEventListener('click', preventEvent, true)
		window.removeEventListener('touchstart', preventEvent, true)
		window.removeEventListener('pointerdown', preventEvent, true)
	});

	onUnmounted(() => {
		const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
		events.forEach(event => window.removeEventListener(event, resetOnActivity));
		idleStore.clearTimers();
	});
</script>
